import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CalendarModule} from "primeng/calendar";
import dayjs from "dayjs";
import {firstValueFrom, forkJoin} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {MessageService} from "primeng/api";
import {FormsModule} from "@angular/forms";
import {FilterDatesEvent} from "../../../shared/interfaces/database/configuracion";
import {DropdownModule} from "primeng/dropdown";
import {FilterDashboardService} from "./filter-dashboard.service";

@Component({
  selector: 'app-filter-dashboard',
  standalone: true,
  imports: [
    CalendarModule,
    FormsModule,
    DropdownModule
  ],
  template: `
    <div class="gap-5 col-12 flex md:inline-flex flex-wrap md:flex-nowrap dash-topbar"  >
      <h3 class="my-auto mr-4 dash-title font-normal">{{ translations.filter }}</h3>

      @if (showGranularity) {
        <p-dropdown [options]="granularityOptions" optionLabel="name" optionValue="value"
                    [placeholder]="translations.placeholderGranularity"
                    (onChange)="changeDates()"
                    [(ngModel)]="granularity"></p-dropdown>
      }

      <p-calendar [placeholder]="translations.placeholderStartDate"
                  [showIcon]="true" [maxDate]="maxDate" dateFormat="dd/mm/yy"
                  [iconDisplay]="'input'" [(ngModel)]="startDate" [minDate]="minDate"
                  (change)="changeDates($event)"
                  (onSelect)="changeDates($event)"></p-calendar>

      <p-calendar [placeholder]="translations.placeholderEndDate"
                  [showIcon]="true" [maxDate]="maxDate" dateFormat="dd/mm/yy"
                  [iconDisplay]="'input'" [(ngModel)]="endDate"
                  (change)="changeDates($event)"
                  (onSelect)="changeDates($event)"></p-calendar>

      @if (activeSuggestion) {
        <p-dropdown [options]="datesSuggestions" optionLabel="name"
                    [showClear]="true"
                    (onChange)="onSelectTypeDate()" optionValue="value"
                    [placeholder]="translations.placeholderdDateSuggestion"
                    [(ngModel)]="dateSuggestion"></p-dropdown>
      }

      <ng-content></ng-content>

    </div>
  `,
  styles: `::ng-deep .p-dropdown {
    height: 100%;
    width: 14rem;
  }`
})
export class FilterDashboardComponent implements OnInit {

  @Output() changesDates: EventEmitter<FilterDatesEvent> = new EventEmitter();

  @Input('emitOnInit') emitOnInit: boolean = false;
  @Input('suggestions') activeSuggestion: boolean = false;
  @Input('showGranularity') showGranularity: boolean = false;
  startDate?: Date;
  endDate: Date = new Date();
  maxDate: Date = new Date();
  granularity: 'day' | 'week' | 'month' = 'week';
  translations: any = {};

  datesSuggestions: any[] = [];
  dateSuggestion: any;
  granularityOptions: any[] = [];

  constructor(private _t: TranslateService, public messageService: MessageService,
              private filterService:FilterDashboardService) {
  }

  ngOnInit(): void {
    this.initTranslations()
      .then(() => {
        this.initDatesSuggestions();
        this.initGranularityOptions();
      });
    if (this.emitOnInit) {
      this.initValues();
      this.changeDates();
    }
  }


  //#region configuracion
  private initDatesSuggestions() {
    this.datesSuggestions = [
      {value: dayjs().startOf('year').toDate(), name: this.translations.currentYear},
      // {value: dayjs().subtract(12, 'months').toDate(), name: this.translations.lastYear},
      {value: dayjs().subtract(6, 'months').toDate(), name: this.translations.lastSixMonths},
    ]
  }

  private initGranularityOptions() {
    this.granularityOptions = [
      {value: 'day', name: this.translations.granularityDay},
      {value: 'week', name: this.translations.granularityWeek},
      {value: 'month', name: this.translations.granularityMonth},
    ]
  }

  private initValues() {
    this.startDate = (this.filterService.startDate() != null) ? this.filterService.startDate() as Date : dayjs().startOf('year').toDate();
    this.endDate = new Date();
  }

  private async initTranslations() {
    this.translations = await firstValueFrom(forkJoin({
      filter: this._t.get('components.filter-dashboard.filtros'),
      warnSummary: this._t.get('form.msg.warn-summary'),
      detailWarn: this._t.get('components.filter-dashboard.advertencia-detalle'),
      detailWarnFechaInicial: this._t.get('components.filter-dashboard.advertencia-fecha-inicial'),
      placeholderStartDate: this._t.get('components.filter-dashboard.placeholder-fecha-inicial'),
      placeholderEndDate: this._t.get('components.filter-dashboard.placeholder-fecha-final'),
      placeholderdDateSuggestion: this._t.get('components.filter-dashboard.placeholder-sugerencia-fechas'),
      placeholderGranularity: this._t.get('components.filter-dashboard.placeholder-granularidad'),
      lastSixMonths: this._t.get('components.filter-dashboard.ultimos-seis-meses'),
      lastYear: this._t.get('components.filter-dashboard.ultimo-anio'),
      currentYear: this._t.get('components.filter-dashboard.anio-en-curso'),
      granularityDay: this._t.get('components.filter-dashboard.granularidad-dia'),
      granularityWeek: this._t.get('components.filter-dashboard.granularidad-semana'),
      granularityMonth: this._t.get('components.filter-dashboard.granularidad-mes'),
    }));
  }

  //#endregion configuracion


  onSelectTypeDate() {
    this.startDate = this.dateSuggestion;
    this.changeDates();
  }


  changeDates(event?:any) {
    if (event) {
      this.dateSuggestion = null;
    }
    if (!this.startDate) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translations.warnSummary,
        detail: this.translations?.detailWarnFechaInicial
      });
      return;
    }
    if (this.endDate < this.startDate) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translations.warnSummary,
        detail: this.translations?.detailWarn
      });
      return;
    }

    this.changesDates.emit({startDate: this.startDate, endDate: this.endDate, granularity: this.granularity});

  }

  get minDate(): Date | null {
    return this.filterService.minDate();
  }
}
